'use strict';

import React, {Component} from 'react';
import {observer} from "mobx-react";
import PropTypes from 'prop-types';

import {FaExclamationCircle, FaCheck} from "react-icons/fa";

import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import ButtonToolbar from "react-bootstrap/ButtonToolbar";
import Spinner from 'react-bootstrap/Spinner';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import removeCourseFromPlanFormStore from './RemoveCourseFromPlanFormStore';
import TermUtils from "@uw-it-sis/lib-react/lib/TermUtils";
import {isEmpty} from "@uw-it-sis/lib-react/lib/Utils";
import {getGeneralPlaceholderName} from "./GeneralPlaceHolderConstants";
import {MetricLabels} from "./Constants";

class RemoveCourseFromPlanForm extends Component {

    constructor(props) {
        super(props);
        this.props = props;
        this.store = removeCourseFromPlanFormStore;

        this.analyticsStore = appStore.getAnalyticsStore();

        this.handleCancelClick = this.handleCancelClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
    }

    componentDidMount() {
        this.store.mount(this.props.planItemId);
    }

    componentWillUnmount() {
        this.store.unmount();
    }

    /**
     * Callback to the parent component when the cancel link is clicked.
     */
    handleCancelClick() {
        this.props.onCancel();
    }

    onSubmit(e) {
        e.preventDefault();  //  Prevent POST and reload.
        this.store.removeCourse();

        this.additionalAnalytics = this.props.additionalAnalytics && Object.keys(this.props.additionalAnalytics).length
            ? this.props.additionalAnalytics : {};
        let appContext = "";
        if(this.additionalAnalytics) {
            appContext = this.additionalAnalytics.context;
        }
        const labels = this.analyticsStore.label(MetricLabels.appContextName, appContext).get();
        this.analyticsStore.addEvent("Remove-PlanItem", labels, {qtr: this.props.termId, ...this.additionalAnalytics});
    }

    render() {
        // item name used in the text of the modal to identify item being moved
        let itemName;
        // item label for the button action, typically itemName = itemLabel, except if item is other placeholder
        let itemLabel;
        if (this.props.data.placeHolder) {
            itemName = getGeneralPlaceholderName(this.props.data.placeHolderCode, this.props.data.placeHolderValue);
            itemLabel = getGeneralPlaceholderName(this.props.data.placeHolderCode, this.props.data.placeHolderValue, false);
            if ( this.props.data.planItemDataObject && this.props.data.planItemDataObject.refObjType === "uw.course.genedrequirement" ) {
                let placeholderPrefix = "Gen Ed Req: ";
                itemName = placeholderPrefix + itemName;
                itemLabel = placeholderPrefix + itemLabel;
            }
        } else {
            itemName = this.props.isSection ? `${this.props.data.courseCode.replace(/\\s\\s+/g, ' ') + " " + this.props.data.code}` : this.props.data.courseDetails.code.replace(/\s\s+/g, ' ');
            itemLabel = itemName;
        }
        // Successful remove display for modal
        if ( this.store.isSubmitted() && isEmpty(this.store.getRemoveFailedMessage()) && !this.store.isRemoving() ) {
            return (
                <>
                    <div className="d-flex align-items-center text-success mb-3">
                        <FaCheck className="me-3 fs-5" aria-hidden={true} />
                        <span>{itemName} has been deleted.</span>
                    </div>
                    <ButtonToolbar className="justify-content-end mt-3">
                        <Button variant="primary" id="remove-planitem-done" onClick={this.handleCancelClick}>Done</Button>
                    </ButtonToolbar>
                </>
            );
        }

        // Build messaging for if there was a failure to remove
        let failedToRemoveMessage;
        if ( !isEmpty(this.store.getRemoveFailedMessage()) ) {
            failedToRemoveMessage =
                <Alert variant="danger" className="mb-0 mt-3">
                    <FaExclamationCircle className="me-2" aria-hidden={true} />
                    {this.store.getRemoveFailedMessage()}
                </Alert>
        }

        // More code to alter when other plan item types come into play.
        let secondaryMessaging = null;
        if ( this.props.isSection ) {
            if ( this.props.data && this.props.data.primary ) {
                secondaryMessaging = this.props.data.hasPlannedSecondary ? <p>Any sections you have selected for this course, such as labs or quizzes, will also be deleted.</p> : null;
            } else {
                secondaryMessaging = <p>You may need to select another {this.props.data.activityOfferingType} section in order to register.</p>;
            }
        }

        if ( this.props.data.isClustered && this.props.isSection) {
            let clusterName = this.props.data.blockRegistration.type === "FIG" ? this.props.data.blockRegistration.type : "cluster";
            secondaryMessaging = <p>All other courses associated with this {clusterName} will also be deleted.</p>;
        }

        // Button labeling for remove action that changes when in a pending state of removing
        let removeButtonLabel = <>Delete {itemLabel}</>;
        if ( this.store.isRemoving() ) {
            removeButtonLabel =
                <>
                    <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2 align-middle"
                    />
                    <span className="align-middle">Deleting {itemLabel}</span>
                </>
        }

        return (
            <>
                <p>You are deleting {itemName} from {TermUtils.makeTermDescriptionFromTermId(this.props.termId)}.</p>
                {secondaryMessaging}
                <ButtonToolbar className="justify-content-end mt-3">
                    <Button id="remove-planitem-submit"
                            variant="primary"
                            onClick={this.store.isRemoving() ? null : this.onSubmit}
                            disabled={this.store.isRemoving()}>
                        {removeButtonLabel}
                    </Button>
                    <Button id="remove-planitem-cancel" className="btn-secondary" onClick={this.handleCancelClick} type="reset">Cancel</Button>
                </ButtonToolbar>
                <div role="alert" aria-live="assertive" aria-atomic="true">
                    {failedToRemoveMessage}
                </div>
            </>
        );
    }
}

RemoveCourseFromPlanForm.propTypes = {
    planItemId: PropTypes.string.isRequired,
    termId: PropTypes.string.isRequired,
    // Going to have to refactor this later when items other then course and sections come into play
    data: PropTypes.object.isRequired,
    isSection: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    additionalAnalytics: PropTypes.object
};

export default observer(RemoveCourseFromPlanForm);